import type * as Common from '@piccolohealth/pbs-common';
import { FeatureFlag, LabelType, Role, renderLabelType } from '@piccolohealth/pbs-common';
import { ChakraV3, Spin } from '@piccolohealth/ui';
import React from 'react';
import { FormSection } from '../../components/forms/FormSection';
import { PiccoloError } from '../../components/generic/PiccoloError';
import { useLabelsQuery } from '../../graphql/hooks/useLabelsQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { useRole } from '../../hooks/useRole';
import { LabelsForType } from './labels/LabelsForType';

export const LabelSettings = () => {
  const { organization } = useAppContext();

  const hasSupportRole = useRole(Role.Support);
  const hasScoringPermission =
    organization.hasFeature(FeatureFlag.TemplateScoring) || hasSupportRole;

  const labelTypes = hasScoringPermission
    ? Object.values(LabelType)
    : Object.values(LabelType).filter((type) => type !== LabelType.ScoringCriteria);

  const { data, isLoading, error } = useLabelsQuery({
    organizationId: organization.id,
    type: labelTypes,
  });

  const labels = (data?.organization?.labels ?? []) as unknown as Common.Label[];

  const tabs = labelTypes.map((type) => {
    return (
      <ChakraV3.Tabs.Trigger value={type} key={type}>
        {renderLabelType(type)}
      </ChakraV3.Tabs.Trigger>
    );
  });

  const panels = labelTypes.map((type) => {
    const filteredLabels = labels.filter((label) => label.type === type);
    return (
      <ChakraV3.Tabs.Content value={type} key={type}>
        <LabelsForType type={type} labels={filteredLabels} />
      </ChakraV3.Tabs.Content>
    );
  });

  if (isLoading) {
    return <Spin showAfterDuration={2000} />;
  }

  if (error) {
    return <PiccoloError error={error} />;
  }

  return (
    <FormSection heading='Labels' description='Create and edit different labels' maxW='4xl'>
      <ChakraV3.Separator />
      <ChakraV3.Stack>
        <ChakraV3.Tabs.Root
          variant='subtle'
          orientation='vertical'
          defaultValue={labelTypes[0]}
          size='sm'
          w='full'
          h='full'
        >
          <ChakraV3.Tabs.List minW='120px' flexShrink={0}>
            {tabs}
          </ChakraV3.Tabs.List>
          <ChakraV3.Stack
            minH='md'
            minW='lg'
            borderLeftColor='gray.200'
            borderLeftWidth='1px'
            pl={6}
            ml={6}
          >
            <ChakraV3.Tabs.ContentGroup overflowY='auto' h='full' w='full'>
              {panels}
            </ChakraV3.Tabs.ContentGroup>
          </ChakraV3.Stack>
        </ChakraV3.Tabs.Root>
      </ChakraV3.Stack>
    </FormSection>
  );
};
